import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext.jsx';
import ProtectedRoute from './auth/ProtectedRoute.jsx';
import AppLayout from './layouts/AppLayout.jsx';
import { homeForRole } from './layouts/navigation.js';

import LoginPage from './pages/LoginPage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import CustomerListPage from './pages/CustomerListPage.jsx';
import CustomerDetailPage from './pages/CustomerDetailPage.jsx';
import ProfilePage from './pages/ProfilePage.jsx';
import NotFoundPage, { ForbiddenPage } from './pages/NotFoundPage.jsx';
import LeadDetailPage from './pages/LeadDetailPage.jsx';
import TasksPage from './pages/TasksPage.jsx';
import ChatPage from './pages/ChatPage.jsx';
import DocumentListPage from './pages/documents/DocumentListPage.jsx';
import DocumentEditorPage from './pages/documents/DocumentEditorPage.jsx';
import DocumentPrintPage from './pages/documents/DocumentPrintPage.jsx';
import ProductsPage from './pages/admin/ProductsPage.jsx';

import QualificationFormPage from './pages/telepro/QualificationFormPage.jsx';
import LeadListPage from './pages/telepro/LeadListPage.jsx';
import BookMeetingPage from './pages/telepro/BookMeetingPage.jsx';
import MyAgendaPage from './pages/MyAgendaPage.jsx';

import PipelinePage from './pages/commercial/PipelinePage.jsx';
import CalendarPage from './pages/commercial/CalendarPage.jsx';
import AvailabilityPage from './pages/commercial/AvailabilityPage.jsx';

import ItDashboardPage from './pages/it/ItDashboardPage.jsx';
import ProjectsPage from './pages/it/ProjectsPage.jsx';

import UsersPage from './pages/admin/UsersPage.jsx';
import StatsPage from './pages/admin/StatsPage.jsx';
import AgenciesPage from './pages/admin/AgenciesPage.jsx';
import TranslationsPage from './pages/admin/TranslationsPage.jsx';
import SettingsPage from './pages/admin/SettingsPage.jsx';
import AuditLogPage from './pages/admin/AuditLogPage.jsx';
// The platform screens: about the IT role, but only an administrator needs them.
import SystemHealthPage from './pages/admin/SystemHealthPage.jsx';
import IntegrationsPage from './pages/admin/IntegrationsPage.jsx';
import CustomerOverviewPage from './pages/admin/CustomerOverviewPage.jsx';
import ReceivablesPage from './pages/admin/ReceivablesPage.jsx';

const HomeRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={homeForRole(user.role)} replace />;
};

/**
 * Route tree. Each category branch is guarded by role; the API applies the same
 * rules again, so a hand-typed URL cannot leak data.
 */
const App = () => (
  <Routes>
    <Route path="/login" element={<LoginPage />} />

    {/* Printable documents render on their own, without the app shell. */}
    <Route element={<ProtectedRoute roles={['COMMERCIAL', 'ADMIN']} />}>
      <Route path="/documents/:id/print" element={<DocumentPrintPage />} />
    </Route>

    <Route element={<ProtectedRoute />}>
      <Route element={<AppLayout />}>
        <Route index element={<HomeRedirect />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/customers/:id" element={<CustomerDetailPage />} />
        <Route path="/leads/:id" element={<LeadDetailPage />} />
        <Route path="/tasks" element={<TasksPage />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/403" element={<ForbiddenPage />} />

        {/* --- Teleprospection --- */}
        <Route element={<ProtectedRoute roles={['TELEPRO', 'ADMIN']} />}>
          <Route path="/telepro" element={<DashboardPage />} />
          <Route path="/telepro/qualification" element={<QualificationFormPage />} />
          <Route path="/telepro/qualification/:id" element={<QualificationFormPage />} />
          <Route path="/telepro/leads" element={<LeadListPage titleKey="nav.myLeads" newLeadButton />} />
          <Route
            path="/telepro/prospects"
            element={
              <CustomerListPage
                titleKey="nav.myProspects"
                subtitleKey="customers.toCallHint"
                showOwners={false}
                baseQuery={{ qualified: 'no' }}
                importable
                callable
              />
            }
          />
          <Route path="/telepro/pipeline" element={<PipelinePage />} />
          <Route path="/telepro/book" element={<BookMeetingPage />} />
          <Route path="/telepro/agenda" element={<MyAgendaPage />} />
        </Route>

        {/* --- Commercial --- */}
        <Route element={<ProtectedRoute roles={['COMMERCIAL', 'ADMIN']} />}>
          <Route path="/commercial" element={<DashboardPage />} />
          <Route path="/commercial/pipeline" element={<PipelinePage />} />
          {/* The rep's own list is where signing off happens, so it shows the
              column: it is the screen you go to when IT asks for an account. */}
          <Route
            path="/commercial/customers"
            element={<CustomerListPage titleKey="nav.myCustomers" showSignature />}
          />
          <Route path="/commercial/calendar" element={<CalendarPage />} />
          <Route path="/commercial/availability" element={<AvailabilityPage />} />
        </Route>

        {/* --- Quotes and invoices ---
            Sales and administration both work here. The API scopes a rep to the
            paperwork of their own customers; an administrator sees the lot. */}
        <Route element={<ProtectedRoute roles={['COMMERCIAL', 'ADMIN']} />}>
          <Route path="/documents" element={<DocumentListPage />} />
          <Route path="/documents/new" element={<DocumentEditorPage />} />
          <Route path="/documents/:id" element={<DocumentEditorPage />} />
        </Route>

        {/* --- Agency --- */}
        <Route element={<ProtectedRoute roles={['AGENCY', 'ADMIN']} />}>
          <Route path="/agency" element={<DashboardPage />} />
          <Route path="/agency/customers" element={<CustomerListPage titleKey="nav.agencyCustomers" />} />
          <Route path="/agency/pipeline" element={<PipelinePage />} />
          <Route path="/agency/calendar" element={<CalendarPage />} />
          <Route path="/agency/team" element={<UsersPage />} />
          <Route path="/agency/stats" element={<StatsPage />} />
        </Route>

        {/* --- IT ---
            Its own dashboard rather than the shared one: this team's screen is
            about delivery, not about the sales funnel. */}
        <Route element={<ProtectedRoute roles={['IT', 'ADMIN']} />}>
          <Route path="/it" element={<ItDashboardPage />} />
          <Route path="/it/projects" element={<ProjectsPage />} />
          <Route
            path="/it/customers"
            element={
              <CustomerListPage
                titleKey="nav.allCustomers"
                subtitleKey="customers.signedOnlyHint"
                showSignature
              />
            }
          />
        </Route>

        {/* --- Admin --- */}
        <Route element={<ProtectedRoute roles={['ADMIN']} />}>
          {/* One dashboard, not four. /telepro, /commercial and /agency render
              this same page and, for an administrator, the same company-wide
              payload - so the admin menu points here and drops the other three. */}
          <Route path="/admin" element={<DashboardPage />} />
          {/* The whole book in one table, and the money owed on it. Neither is a
              place to prospect from - see layouts/navigation.js. */}
          <Route path="/admin/customers" element={<CustomerOverviewPage />} />
          <Route path="/admin/receivables" element={<ReceivablesPage />} />
          <Route path="/admin/pipeline" element={<PipelinePage />} />
          <Route path="/admin/stats" element={<StatsPage />} />
          <Route path="/admin/agencies" element={<AgenciesPage />} />
          <Route path="/admin/products" element={<ProductsPage />} />
          <Route path="/admin/users" element={<UsersPage />} />
          <Route path="/admin/settings" element={<SettingsPage />} />
          <Route path="/admin/translations" element={<TranslationsPage />} />
          <Route path="/admin/audit" element={<AuditLogPage />} />
          {/* Platform diagnostics, moved out of the IT menu: the team that uses
              this CRM to deliver projects does not need a permission matrix or
              a list of connectors nobody has wired yet. */}
          <Route path="/admin/health" element={<SystemHealthPage />} />
          <Route path="/admin/integrations" element={<IntegrationsPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Route>
  </Routes>
);

export default App;
